import type { ChapterData } from "../types/chapter";
import type { LevelData } from "../types/level";
import type { GameSave } from "../types/save";
import type { SymbolDictionaryData } from "../types/symbol";
import {
  createDefaultGameSave,
  validateChapterData,
  validateGameSave,
  validateLevelData,
  validateSymbolDictionaryData,
} from "./validation";

// Raw JSON imports — only AssetLoader reads raw JSON in the codebase.
import rawSymbols from "../data/symbols.json";
import rawChapter1 from "../data/chapters/chapter-1.json";
import rawChapter2 from "../data/chapters/chapter-2.json";
import rawChapter3 from "../data/chapters/chapter-3.json";
import rawChapter4 from "../data/chapters/chapter-4.json";
import rawChapterEnd from "../data/chapters/chapter-end.json";
import rawLevelCh101 from "../data/levels/ch1-01.json";
import rawLevelCh102 from "../data/levels/ch1-02.json";
import rawLevelCh103 from "../data/levels/ch1-03.json";
import rawLevelCh104 from "../data/levels/ch1-04.json";
import rawLevelCh201 from "../data/levels/ch2-01.json";
import rawLevelCh202 from "../data/levels/ch2-02.json";
import rawLevelCh203 from "../data/levels/ch2-03.json";
import rawLevelCh204 from "../data/levels/ch2-04.json";
import rawLevelCh205 from "../data/levels/ch2-05.json";
import rawLevelCh301 from "../data/levels/ch3-01.json";
import rawLevelCh302 from "../data/levels/ch3-02.json";
import rawLevelCh303 from "../data/levels/ch3-03.json";
import rawLevelCh304 from "../data/levels/ch3-04.json";
import rawLevelCh401 from "../data/levels/ch4-01.json";
import rawLevelCh402 from "../data/levels/ch4-02.json";
import rawLevelCh403 from "../data/levels/ch4-03.json";
import rawLevel001 from "../data/levels/level-0-1.json";
import rawLevel002 from "../data/levels/level-0-2.json";
import rawLevel003 from "../data/levels/level-0-3.json";
import rawLevel0 from "../data/levels/level-0.json";
import rawEnding0 from "../data/levels/ending-0.json";
import rawEnding1 from "../data/levels/ending-1.json";
import rawEnding2 from "../data/levels/ending-2.json";
import rawEnding3 from "../data/levels/ending-3.json";

export interface ValidatedAssets {
  readonly symbols: SymbolDictionaryData;
  readonly chapters: Readonly<Record<string, ChapterData>>;
  readonly levels: Readonly<Record<string, LevelData>>;
  readonly save: GameSave;
}

export interface RawAssetBundle {
  readonly symbols: unknown;
  readonly chapters: Readonly<Record<string, unknown>>;
  readonly levels: Readonly<Record<string, unknown>>;
  readonly save?: unknown;
}

function validateRawAssets(raw: RawAssetBundle): ValidatedAssets {
  const symbols = validateSymbolDictionaryData(raw.symbols);

  const chapters: Record<string, ChapterData> = {};
  for (const [key, value] of Object.entries(raw.chapters)) {
    chapters[key] = validateChapterData(value);
  }

  const levels: Record<string, LevelData> = {};
  for (const [key, value] of Object.entries(raw.levels)) {
    levels[key] = validateLevelData(value);
  }

  const save = raw.save !== undefined
    ? validateGameSave(raw.save)
    : createDefaultGameSave();

  return { symbols, chapters, levels, save };
}

export function getCanonicalRawAssetBundle(): RawAssetBundle {
  return {
    symbols: rawSymbols,
    chapters: {
      "chapter-1": rawChapter1,
      "chapter-2": rawChapter2,
      "chapter-3": rawChapter3,
      "chapter-4": rawChapter4,
      "chapter-end": rawChapterEnd,
    },
    levels: {
      "ch1-01": rawLevelCh101,
      "ch1-02": rawLevelCh102,
      "ch1-03": rawLevelCh103,
      "ch1-04": rawLevelCh104,
      "ch2-01": rawLevelCh201,
      "ch2-02": rawLevelCh202,
      "ch2-03": rawLevelCh203,
      "ch2-04": rawLevelCh204,
      "ch2-05": rawLevelCh205,
      "ch3-01": rawLevelCh301,
      "ch3-02": rawLevelCh302,
      "ch3-03": rawLevelCh303,
      "ch3-04": rawLevelCh304,
      "ch4-01": rawLevelCh401,
      "ch4-02": rawLevelCh402,
      "ch4-03": rawLevelCh403,
      "level-0-1": rawLevel001,
      "level-0-2": rawLevel002,
      "level-0-3": rawLevel003,
      "level-0": rawLevel0,
      "ending-0": rawEnding0,
      "ending-1": rawEnding1,
      "ending-2": rawEnding2,
      "ending-3": rawEnding3,
    },
  };
}

const CANONICAL_RAW_BUNDLE: RawAssetBundle = getCanonicalRawAssetBundle();

export function loadValidatedAssets(raw: RawAssetBundle = CANONICAL_RAW_BUNDLE): ValidatedAssets {
  return validateRawAssets(raw);
}

export function loadValidatedAssetsFromRaw(raw: RawAssetBundle): ValidatedAssets {
  return validateRawAssets(raw);
}
